import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Search, PlayCircle, ExternalLink, BookOpen } from 'lucide-react';
import { bibleGatewayUrl } from '../data/bibleBookVideos';

/**
 * Grid of small YouTube embeds, one per Bible book.
 * Videos are lazy-loaded: shows a thumbnail + play button until clicked,
 * then swaps in the real iframe. Keeps the modal fast even with 40+ videos.
 *
 * Props:
 *   open         : bool (dialog open state)
 *   onOpenChange : (bool) => void
 *   title        : e.g. "Old Testament — Book by Book"
 *   videos       : [{ id, title, duration }]
 *   playlistUrl  : optional link to the full YouTube playlist
 *   accent       : Tailwind text color class for the header title
 */
const BibleBooksDialog = ({ open, onOpenChange, title, videos, playlistUrl, accent = 'text-purple-700', initialSearch = '', subtitle, searchPlaceholder = 'Search a book (e.g. Genesis, Psalms, John)', initialVideoId = '' }) => {
  const [q, setQ] = useState('');
  const [playing, setPlaying] = useState({}); // id -> true when user clicked play

  // Sync incoming initialSearch (e.g. deep-linked from Global Search) whenever the dialog opens.
  React.useEffect(() => {
    if (open) setQ(initialSearch || '');
  }, [open, initialSearch]);

  // If caller passed `initialVideoId` (e.g., "this Sunday's video"), auto-scroll to it
  // and start playing after the dialog paints.
  React.useEffect(() => {
    if (!open || !initialVideoId) return;
    const tryScroll = (attempts = 0) => {
      const el = document.querySelector(`[data-testid="bible-book-card-${initialVideoId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPlaying((p) => ({ ...p, [initialVideoId]: true }));
      } else if (attempts < 15) {
        setTimeout(() => tryScroll(attempts + 1), 100);
      }
    };
    setTimeout(() => tryScroll(), 250);
  }, [open, initialVideoId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return videos;
    return videos.filter((v) => v.title.toLowerCase().includes(term));
  }, [q, videos]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <DialogTitle className={`text-2xl font-heading ${accent} flex items-center gap-2`}>
                <PlayCircle className="w-6 h-6" />
                {title}
              </DialogTitle>
              <DialogDescription>
                {subtitle || `A short animated overview of every book. Tap any thumbnail to play. ${videos.length} videos.`}
              </DialogDescription>
            </div>
            {playlistUrl && (
              <a
                href={playlistUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm self-start"
                data-testid="bible-books-playlist-link"
              >
                <ExternalLink className="w-4 h-4" />
                YouTube Playlist
              </a>
            )}
          </div>
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-10"
              data-testid="bible-books-search"
            />
          </div>
        </DialogHeader>

        <div className="p-4 overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-12">
              No book matches &quot;{q}&quot;. Try a different search.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto" data-testid="bible-books-grid">
              {filtered.map((v) => {
                const isPlaying = playing[v.id];
                return (
                  <div
                    key={v.id}
                    className="rounded-lg overflow-hidden border border-slate-200 bg-white hover:shadow-md transition-shadow"
                    data-testid={`bible-book-card-${v.id}`}
                  >
                    <div className="aspect-video w-full bg-black relative">
                      {isPlaying ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${v.id}?autoplay=1`}
                          title={v.title}
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPlaying((p) => ({ ...p, [v.id]: true }))}
                          className="group w-full h-full relative block"
                          aria-label={`Play: ${v.title}`}
                          data-testid={`bible-book-play-${v.id}`}
                        >
                          <img
                            src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                            alt={v.title}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-red-600/95 group-hover:scale-110 transition-transform flex items-center justify-center">
                              <PlayCircle className="w-7 h-7 text-white" fill="currentColor" strokeWidth={0} />
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-sm font-medium text-slate-800 leading-tight truncate" title={v.title}>
                        {v.title}
                      </p>
                      <div className="flex items-center justify-between mt-0.5">
                        <p className="text-xs text-slate-400">{v.duration}</p>
                        {v.book && (
                          <a
                            href={bibleGatewayUrl(v.book)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 hover:underline"
                            title={`Read ${v.book} on Bible Gateway`}
                            data-testid={`read-book-${v.id}`}
                          >
                            <BookOpen className="w-3 h-3" />
                            Read this book
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BibleBooksDialog;
